import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/admin`;

const ADMIN_TOKEN_KEY = 'waan_admin_token';
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY) || '';
export const setAdminToken = (t) => localStorage.setItem(ADMIN_TOKEN_KEY, t);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);

const client = axios.create({ baseURL: API, timeout: 60000 });
client.interceptors.request.use((cfg) => {
  const t = getAdminToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export async function adminLogin(email, password) {
  const res = await client.post('/login', { email, password });
  if (res.data?.token) setAdminToken(res.data.token);
  return res.data;
}
export async function adminMe() { const res = await client.get('/me'); return res.data; }
export async function adminChangePassword(currentPassword, newPassword) {
  const res = await client.post('/change-password', { currentPassword, newPassword });
  return res.data;
}
export async function adminStats() { const res = await client.get('/stats'); return res.data; }

export async function listApplications(params) {
  const res = await client.get('/applications', { params });
  return res.data;
}
export async function getApplication(ref) { const res = await client.get(`/applications/${ref}`); return res.data; }
export async function updateStatus(ref, status, note, notifyTraveler = true) {
  const res = await client.patch(`/applications/${ref}/status`, { status, note, notifyTraveler });
  return res.data;
}
export async function addNote(ref, text) {
  const res = await client.post(`/applications/${ref}/notes`, { text });
  return res.data;
}
export async function refundApplication(ref, { amount = null, reason = 'requested_by_customer', admin_notes = '' } = {}) {
  const res = await client.post(`/applications/${ref}/refund`, { amount, reason, admin_notes });
  return res.data;
}
export async function getRefundPolicy(ref) {
  const res = await client.get(`/applications/${ref}/refund-policy`);
  return res.data;
}
export async function getRefundAnalytics() {
  const res = await client.get('/refunds/analytics');
  return res.data;
}
export function refundCsvExportUrl(start, end) {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const qs = params.toString();
  return `${API}/refunds/export.csv${qs ? `?${qs}` : ''}`;
}
export async function downloadRefundCsv(start, end) {
  const params = {};
  if (start) params.start = start;
  if (end) params.end = end;
  const res = await client.get('/refunds/export.csv', { params, responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  const dt = new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 14);
  a.href = url; a.download = `visasg_refunds_${dt}.csv`;
  document.body.appendChild(a); a.click();
  setTimeout(() => { window.URL.revokeObjectURL(url); a.remove(); }, 100);
}

export async function adminForgotPassword(email) {
  const res = await client.post('/forgot-password', { email });
  return res.data;
}
export async function adminResetPassword(token, password) {
  const res = await client.post('/reset-password', { token, password });
  return res.data;
}
export function fileDownloadUrl(ref, fileId) {
  return `${API}/applications/${ref}/files/${fileId}`;
}
export async function downloadFile(ref, fileId, filename) {
  // Try the JSON path first (S3 signed URL). If the server proxies the file
  // instead (local storage), it returns a blob content-type.
  const res = await client.get(`/applications/${ref}/files/${fileId}`, { responseType: 'blob' });
  const ct = res.headers['content-type'] || '';
  if (ct.includes('application/json')) {
    // Backend returned {url, expiresIn, filename} - open the signed URL in a new tab.
    const txt = await res.data.text();
    const meta = JSON.parse(txt);
    if (meta.url) {
      window.open(meta.url, '_blank', 'noopener');
      return;
    }
  }
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'file'; document.body.appendChild(a); a.click();
  window.URL.revokeObjectURL(url); a.remove();
}
export async function downloadReceipt(ref) {
  const res = await client.get(`/applications/${ref}/receipt`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url; a.download = `WaanTravel-Receipt-${ref}.pdf`; document.body.appendChild(a); a.click();
  window.URL.revokeObjectURL(url); a.remove();
}
export async function regenerateReceipt(ref) {
  const res = await client.post(`/applications/${ref}/receipt/regenerate`);
  return res.data;
}
export function exportApplicationsCsvUrl() { return `${API}/applications/export/csv`; }
export async function downloadApplicationsCsv() {
  const res = await client.get('/applications/export/csv', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url; a.download = `applications-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a); a.click();
  window.URL.revokeObjectURL(url); a.remove();
}

export async function listCustomers() { const res = await client.get('/customers'); return res.data; }
export async function customerDetail(email) { const res = await client.get(`/customers/${encodeURIComponent(email)}`); return res.data; }
// Preview + delete for a customer's linked applications/files. Returns
// `{ok, applications:[...], totalApplications, totalFiles, hasSuccessfulPayment,
// hasProcessingApplication, contactRecords, eligibleForDeletion, blockedReason}`.
export async function customerDeletionImpact(email) { const res = await client.get(`/customers/${encodeURIComponent(email)}/deletion-impact`); return res.data; }
export async function deleteCustomer(email) { const res = await client.delete(`/customers/${encodeURIComponent(email)}`); return res.data; }
export async function applicationDeletionImpact(ref) { const res = await client.get(`/applications/${ref}/deletion-impact`); return res.data; }
export async function deleteApplication(ref) { const res = await client.delete(`/applications/${ref}`); return res.data; }
export async function archiveApplication(ref) { const res = await client.post(`/applications/${ref}/archive`); return res.data; }
export async function unarchiveApplication(ref) { const res = await client.post(`/applications/${ref}/unarchive`); return res.data; }
// Travel Service Records / ICA Evidence — Phase 1 + Phase 2
// Phase 1 kinds: 'itinerary' | 'transaction-record'
// Phase 2 kinds: 'payment-receipt' | 'booking-summary' | 'service-agreement'
export async function getEvidence(ref) { const res = await client.get(`/applications/${ref}/evidence`); return res.data; }
export async function updateEvidence(ref, payload) { const res = await client.put(`/applications/${ref}/evidence`, payload); return res.data; }
export function evidencePdfUrl(ref, kind) {
  return `${API}/applications/${ref}/evidence/pdf/${kind}`;
}
export async function downloadEvidencePdf(ref, kind, filename, params = {}) {
  const res = await client.get(`/applications/${ref}/evidence/pdf/${kind}`, { responseType: 'blob', params });
  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `${ref}-${kind}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
// Returns an object URL suitable for an <iframe> preview.
// Caller must revoke it via URL.revokeObjectURL when the preview closes.
export async function getEvidencePdfBlobUrl(ref, kind, params = {}) {
  const res = await client.get(`/applications/${ref}/evidence/pdf/${kind}`, { responseType: 'blob', params });
  return URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
}

// Supporting documents (Phase 2)
export async function listEvidenceSupporting(ref) {
  const res = await client.get(`/applications/${ref}/evidence/supporting-documents`);
  return res.data;
}
export async function uploadEvidenceSupporting(ref, file, kind, label = '') {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('kind', kind);
  fd.append('label', label);
  const res = await client.post(`/applications/${ref}/evidence/supporting-documents`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}
export async function downloadEvidenceSupporting(ref, fileId, filename) {
  const res = await client.get(`/applications/${ref}/evidence/supporting-documents/${fileId}`, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || fileId;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export async function deleteEvidenceSupporting(ref, fileId) {
  const res = await client.delete(`/applications/${ref}/evidence/supporting-documents/${fileId}`);
  return res.data;
}

// Applicant Documents Pack (Phase 3) — consolidated ZIP with Document Index cover
export async function downloadEvidencePack(ref, opts = {}) {
  const {
    includeSupporting = true,
    includeItinerary = true,
    includeTransaction = true,
    includeBookingSummary = true,
    includeServiceAgreement = true,
    includeServiceConfirmationLetter = true,
    includePaymentReceipt = true,
    transactionVariant = 'historical',
    serviceAgreementVariant = 'historical',
  } = opts;
  const res = await client.get(`/applications/${ref}/evidence/pack`, {
    responseType: 'blob',
    params: {
      include_supporting: includeSupporting ? 'true' : 'false',
      include_itinerary: includeItinerary ? 'true' : 'false',
      include_transaction: includeTransaction ? 'true' : 'false',
      include_booking_summary: includeBookingSummary ? 'true' : 'false',
      include_service_agreement: includeServiceAgreement ? 'true' : 'false',
      include_service_confirmation_letter: includeServiceConfirmationLetter ? 'true' : 'false',
      include_payment_receipt: includePaymentReceipt ? 'true' : 'false',
      transaction_variant: transactionVariant,
      service_agreement_variant: serviceAgreementVariant,
    },
  });
  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${ref}-Applicant-Documents-Pack.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return {
    entries: parseInt(res.headers['x-evidence-pack-entries'] || '0', 10),
    paymentReceiptIncluded: (res.headers['x-evidence-pack-payment-receipt-included'] || '0') === '1',
  };
}

// Phase 4 — finalise / reopen working draft
export async function finaliseEvidence(ref) {
  const res = await client.post(`/applications/${ref}/evidence/finalise`);
  return res.data;
}
export async function reopenEvidence(ref) {
  const res = await client.post(`/applications/${ref}/evidence/reopen`);
  return res.data;
}
// Bulk cleanup for known test-application refs. Preview runs a dry-run
// eligibility check; the actual delete cascades files + pending_uploads
// + receipt for each accepted ref. `force=true` allows the operator to
// override the safe-by-default 409 protection for records they certify
// are test data even if the payment/status fields say otherwise.
export async function bulkDeleteApplicationsPreview(refs, force = false) {
  const res = await client.post('/applications/bulk-delete-preview', { refs, force });
  return res.data;
}
export async function bulkDeleteApplications(refs, force = false) {
  const res = await client.post('/applications/bulk-delete', { refs, force });
  return res.data;
}
export async function listContacts(params) { const res = await client.get('/contacts', { params }); return res.data; }
export async function updateContactStatus(id, status) { const res = await client.patch(`/contacts/${id}/status`, { status }); return res.data; }
export async function deleteContact(id) { const res = await client.delete(`/contacts/${id}`); return res.data; }
export async function listAffiliatesAdmin(params) { const res = await client.get('/affiliates', { params }); return res.data; }
export async function listPayments(params) { const res = await client.get('/payments', { params }); return res.data; }
export async function listEmails(params) { const res = await client.get('/emails', { params }); return res.data; }
export async function sendManualEmail(to, subject, html) { const res = await client.post('/emails/send', { to, subject, html }); return res.data; }
export async function listAudit(params) { const res = await client.get('/audit', { params }); return res.data; }
export async function cmsGet(key) { const res = await client.get(`/cms/${key}`); return res.data; }
export async function cmsPut(key, value) { const res = await client.put(`/cms/${key}`, value); return res.data; }

// Country toggle (Accept-Applications switch)
export async function adminListCountries() { const res = await client.get('/countries'); return res.data; }
export async function adminToggleCountry(code, enabled, extras = {}) {
  const res = await client.put(`/countries/${code}`, { enabled, ...extras });
  return res.data;
}

// Pricing extras (visa-type addons + fixed extras)
export async function adminGetPricingExtras() { const res = await client.get('/pricing-extras'); return res.data; }
export async function adminSavePricingExtras(payload) { const res = await client.put('/pricing-extras', payload); return res.data; }

export default client;
